/**
 * Fetches dynamic option sets (Area, Bank Name) from the Partner Connect backend,
 * which proxies them from Pipedrive's `dealFields` enums. Used by Step 2
 * (city) and Step 5 (bank) to render dropdowns whose contents stay in sync
 * with the Pipedrive workspace without a frontend redeploy.
 */

import { apiClient } from 'shared/lib/apiClient';

export interface OnboardingOption {
  id: number;
  label: string;
  labelClean: string;
  /**
   * Localized display names for city (area) options, sourced from the haat-hub
   * Partner Connect Management app. Optional because bank options and the
   * Pipedrive fallback do not provide them.
   */
  nameEn?: string | null;
  nameHe?: string | null;
  nameAr?: string | null;
  /**
   * Business category for store-type options (Restaurant / Market), sourced
   * from the haat-hub Partner Connect Management app. `null`/absent when the
   * admin hasn't tagged the store type yet.
   */
  category?: 'restaurant' | 'market' | null;
}

export interface OnboardingOptions {
  areas: OnboardingOption[];
  banks: OnboardingOption[];
  /**
   * Store-type options sourced from the haat-hub Partner Connect Management app
   * (visible only, with localized names). Optional/defaulted so an older backend
   * that does not yet return them degrades to the bundled `STORE_TYPES` list.
   */
  storeTypes?: OnboardingOption[];
}

export const fetchOnboardingOptions = async (): Promise<OnboardingOptions> => {
  const res = await apiClient.get<OnboardingOptions>('/onboarding/options');
  return res.data;
};

export interface PosOption {
  id: string;
  label: string;
}

interface PosSystemsResponse {
  posSystems: PosOption[];
}

/**
 * Fetches the POS-systems list from the backend, which proxies the internal
 * HAAT POS API (so backoffice credentials never reach the browser). Used by the
 * business-questions step when the partner chooses POS integration.
 */
export const fetchPosSystems = async (): Promise<PosOption[]> => {
  const res = await apiClient.get<PosSystemsResponse>('/onboarding/pos-systems');
  return res.data.posSystems ?? [];
};
